/**
 * The `drawings` namespace, in English — C2's browser and its drawers (`KLQ-20260819-005`).
 *
 * Its own namespace per the rule in `lib/i18n/catalog.ts`: a translator finishes one screen
 * family at a time, and the drawings browser is the family the whole product exists for.
 *
 * Copy that is load-bearing rather than decorative, and should not be softened without
 * reading why:
 *
 * - `publish.becomesVersion` / `publish.becomesNew` are the design's promise (`chat1.md` §C4,
 *   `KLQ-20261023-010`): every counter in them comes off the wire, and the match that chose
 *   the drawing is the server's. The client renders no drawing name of its own —
 *   `hierarchy/naming.py` is the only implementation of the grammar.
 * - `publish.model` is the product in one sentence and also the empty state's; it says
 *   *verzia*, not *koncept*, until drafts ship.
 * - `action.download` and not "Open": there is no viewer in this wave. A button promising one
 *   would be the lie the tab row already refuses to tell.
 * - `upload.error.rejected` deliberately does not distinguish "wrong format" from "the scanner
 *   found something". The action is identical — that file cannot go in — and naming the
 *   difference invites reading it as a hint about the contents.
 * - `error.gone` never mentions permissions. An out-of-scope drawing answers `404`
 *   byte-identically to one that never existed, and the server is careful about that.
 */

export const drawingsEn = {
  // The table (`KLQ-20260825-002`). Column headers are the consumer's words; the twisty's
  // own two live in the `app` namespace, where the chrome's copy already sits.
  'table.what': 'Drawing',
  'table.number': 'No.',
  'table.version': 'Version',
  'table.owner': 'Task',

  // Paste-import (`KLQ-20260824-002`). A pasted row is a place and a kind of sheet; the
  // address it gets is minted by the tree, which is why no field here names one.
  'import.field.location': 'Where',
  'import.field.slot': 'Drawing kind',
  'import.reason.noLocation': 'A drawing needs a place.',
  'import.reason.unknownLocation': 'No such place in this project.',
  'import.reason.ambiguousLocation': 'More than one place is called that. Use the full path.',
  'import.reason.noSlot': 'Say which kind of drawing this is.',
  'import.reason.unknownType': '{value} is not a drawing kind this project uses.',
  'import.reason.exists': 'This drawing is already here.',
  'import.reason.notPlanned': 'The plan did not reach this row.',
  'import.sample': 'Addresses will look like {identifier}.',
  'table.status': 'Status',
  'table.flat': 'All drawings',
  'axis.legend': 'Group by',
  'axis.location': 'Location',
  'axis.object': 'Object',
  'table.object': 'Object',
  'table.location': 'Location',
  'bulk.selected': '{count} drawings selected',
  // A ticked level stands for the drawings *loaded* beneath it, and the sentence says so.
  'bulk.selectedLoaded': '{count} drawings selected from those loaded',
  'bulk.selectedBranch': '{count} drawings selected, and everything under the levels you ticked',
  'bulk.menu': 'Drawing actions',
  'bulk.clear': 'Clear selection',
  'bulk.more': 'More',
  'bulk.fileUnder': 'File under object',
  'bulk.fileUnderNone': 'No object',
  'bulk.noObjects': 'This project has no building objects yet. Add them in the project’s shape.',
  'bulk.filed': 'Filed {count} drawings.',
  // Both halves, always: a count of failures alone hides how many worked.
  'bulk.filedSkipped': 'Filed {count} drawings; {skipped} were no longer there.',
  'bulk.filedKept': 'Filed {count} drawings; {kept} kept the object they already had.',
  // The column, when the object a drawing sits under has since been superseded. A word, not a
  // colour: the row is still filed there and still correct.
  'object.archived': '(archived)',
  'object.unknown': '{value} is not an object in this project.',
  'object.archivedRefusal': '{value} is archived. Drawings cannot be filed under it.',
  'object.newTitle': 'New object (SO / PS)',
  'axis.unassigned': 'No object',
  // The same three words the tasks plan prints for a deliverable, because it is the same
  // derivation — spelled out rather than interpolated from the wire value, whose `in_progress`
  // carries an underscore the i18n gates skip.
  'deliverable.planned': 'Planned',
  'deliverable.inProgress': 'In progress',
  'deliverable.done': 'Done',

  // The rail. It slices by location and by nothing else here: a drawing has no assignee, and
  // its status is derived rather than chosen, so a second facet would be a filter pretending
  // to be a place.
  // The slicer's axes (`KLQ-20260820-005` §6). **Which axes exist is the server's
  // answer, not this list** — these are the names a person reads for the fields
  // `GET /slots` publishes today, keyed by the wire field in this app's camelCase.
  // An axis with no key here still appears, named by its own field.
  'slicer.noValues': 'Nothing to slice by here.',
  'slicer.noObjects': 'This project has no building objects yet. Add them in the project’s shape.',
  'slicer.unknownValue': 'something this list cannot name',
  'slicer.under': 'Location',
  'slicer.objectId': 'Object',
  'slicer.disciplineId': 'Discipline',
  'slicer.documentTypeId': 'Kind of sheet',

  'slicer.title': 'Axis',
  'slicer.location': 'Location',
  'slicer.noLocations': 'This project has no levels yet.',
  'under.all': 'Whole project',
  'under.unknown': 'Selected part',

  'detail.close': 'Close',

  'status.loading': 'Loading the drawings…',
  'status.readOnly': 'You can browse and download here. Publishing is for the project team.',

  'action.publish': 'Publish PDF',
  'action.newSlot': 'Plan drawings',
  'action.more': 'More',
  'action.newObject': 'New object',
  'action.newLevel': 'New level',
  'action.rename': 'Rename',
  'action.remove': 'Remove',
  'action.download': 'Download',
  'action.rollback': 'Roll back to this version',
  'action.cancel': 'Cancel',
  'action.publishHere': 'Publish a version here',

  'tree.title': 'Drawings',
  'tree.count': '{count, plural, one {# drawing} other {# drawings}}',
  // The branch's own sentinel row. Present tense: by the time anybody reads it the
  // request is already in flight — it is a status, not a button.
  'tree.more': 'Loading more of this level…',
  'tree.moreCount': '{count, plural, one {# more here} other {# more here}}',
  'tree.explains': 'A drawing is a sheet of the project documentation. Each publish adds a new version.',
  'tree.emptyTitle': 'No drawings here',
  'tree.expandAll': 'Expand all',
  'tree.collapseAll': 'Collapse all',
  'tree.noVersion': 'no versions',
  'tree.version': 'v{counter, number, ::00}',

  // The back-link on a drawing's row (`KLQ-20260824-001`): this drawing is a deliverable of
  // that task. Never worded as if the task were the drawing's address — the identifier is
  // rendered from the tree and never reads the work.
  'tree.owner': 'Task: {name}',
  'tree.ownerClosed': 'Task: {name} (closed)',
  'tree.ownerName': '#{number, number} {title}',
  // Neither named nor linked: the task sits past the page of tasks this screen loaded, or the
  // caller's firm was never admitted to it — and the detail answers `404` to that caller, so
  // this row is printed as plain text rather than as an address nobody can open.
  'tree.ownerUnnamed': 'a task this view cannot name',

  'detail.none': 'No drawing selected',
  'detail.noneBody': 'Pick a drawing on the left to see its versions, or publish a new one.',
  'detail.identifier': 'Drawing ID',
  'detail.formerly': 'Formerly {list}',
  'detail.current': 'Current',
  'detail.publishedBy': 'Published {when}',
  'detail.note': 'Version note',
  'detail.files': 'Files',
  'detail.sourceFile': 'Source file',
  'detail.renditionFile': 'PDF',
  'detail.history': 'Version history',
  'detail.review.open': 'In review',
  'detail.review.approve': 'Approved',
  'detail.review.approve_with_comments': 'Approved with comments',
  'detail.review.request_changes': 'Changes requested',
  'detail.currentBadge': 'Current',
  'detail.supersededBadge': 'Superseded',
  'detail.noVersions': 'No versions published in this drawing yet.',
  'detail.noVersionsCta': 'Publish the first one',

  'history.phase': 'Phase',
  'history.correctPhase': 'Correct phase',
  'history.correctPhaseConfirm': 'Correct to {name}?',
  'history.noPhase': 'No phase',

  'empty.title': 'No drawings yet',
  'empty.body':
    'A drawing is the permanent place for all of its versions. Upload a PDF with the same number and it becomes the next version, and the previous one stays in the history.',

  // The structure offer over a flat list past ten drawings (`KLQ-20261023-011`).
  'structurePrompt.sentence': 'Want to sort the drawings by building and floor?',
  'structurePrompt.action': 'Set up the structure',

  'slots.expected': 'The norm expects this here',
  'slots.notApplicable': 'Not usually part of this phase, but you can still add it',

  'seed.title': 'Set up the structure first',
  'seed.body':
    'A project needs a tree before drawings have anywhere to live. Pick the shape closest to this project. You can rename and rearrange everything afterwards.',
  'seed.apply': 'Use this structure',
  'seed.levels': 'Levels: {levels}',

  'kind.building': 'Building',
  'kind.floor': 'Floor',
  'kind.zone': 'Zone',
  'kind.department': 'Department',
  'kind.room': 'Room',
  'kind.house': 'House',
  'kind.row': 'Row',
  'kind.unit': 'Unit',
  // `kind.stage` is *etapa*, a place in the tree. It is deliberately not called a phase:
  // MCK-20260812-011 spends that word on DÚR/DSP/DPS, which is a property of a drawing.
  'kind.street': 'Street',
  'kind.stage': 'Stage',
  'kind.site': 'Site',
  // Part E — prílohy. A place in the tree, never a filter: nothing is ever moved here by a
  // standing (KLQ-20260827-003 §0).
  'kind.appendix': 'Appendix',

  'level.title': 'New level',
  'level.name': 'Name',
  'level.kind': 'Kind',
  'level.parent': 'Inside',
  'level.parentRoot': 'Top of the project',
  'level.create': 'Add level',

  'slot.title': 'New drawing',
  'slot.name': 'Name',
  'slot.nameHint': 'What this drawing is, like "Level 3 floor plan". The ID is generated from it.',
  'slot.landsInPhase': 'This drawing will be filed under {phase}.',
  'slot.number': 'Drawing number',
  'slot.numberHint': 'Optional. Your own numbering, e.g. A-201.',
  'slot.parent': 'Inside',
  'slot.create': 'Add drawing',

  'publish.title': 'Here is what we read. Anything to fix?',
  'publish.afterTitle': 'Published',
  'publish.published': 'Version published.',
  'publish.model':
    'A drawing is the permanent place for all of its versions. Upload a PDF with the same number and it becomes the next version, and the previous one stays in the history.',
  'publish.rendition': 'PDF',
  'publish.renditionHint': 'One PDF is one version. You can add several at once.',
  'publish.rows': 'Files to publish',
  'publish.col.file': 'File',
  'publish.col.target': 'Goes to',
  'publish.col.outcome': 'What happens',
  'publish.col.sources': 'Source files',
  'publish.target': 'Drawing for {name}',
  'publish.targetPick': 'Choose…',
  'publish.targetNew': 'new drawing',
  'publish.pillNew': 'new drawing',
  'publish.pillVersion': 'version v{counter, number, ::00}',
  'publish.becomesNew': 'A new drawing {label} is created with its first version v01.',
  'publish.becomesNewUnnamed': 'A new drawing is created with its first version v01.',
  'publish.becomesVersion':
    '{label}: this will be version v{counter, number, ::00}; v{previous, number, ::00} stays in the history.',
  'publish.becomesFirst': '{label}: this will be its first version v{counter, number, ::00}.',
  'publish.reading': 'Reading the file names…',
  'publish.sources': 'Source files',
  'publish.sourcesHint':
    'Optional. DWG, RVT, PLN, IFC or images. Stored and versioned alongside the PDF, never opened by us.',
  'publish.addSources': 'Add',
  'publish.addSourcesFor': 'Add source files to {name}',
  'publish.drop': 'Drop PDFs here, or choose them',
  'publish.remove': 'Remove {name}',
  'publish.note': 'Version note',
  'publish.noteHint': 'Optional. Applies to every version you publish here.',
  'publish.submit': 'Publish',
  'publish.hashing': 'Reading {name}…',
  'publish.uploading': 'Uploading {name}…',
  'publish.checking': 'Checking {name}…',
  'publish.ready': '{name} is ready',
  'publish.deduplicated': '{name} was already here, nothing to upload',
  'publish.flagged':
    '{name} contains active content. It will be published and the finding is recorded.',
  'publish.needRendition': 'Choose the PDFs you want to publish.',
  'publish.needTarget': 'Choose where each file goes.',
  'publish.tooMany': 'At most {max, number} files can be published at once.',
  'publish.removeFailed': 'Remove the files that failed to upload.',
  'publish.waitingForFiles': 'Waiting for the PDFs to finish checking.',
  'publish.waitingForSources': 'Waiting for the source files to finish uploading.',
  'publish.phaseLine': 'Recorded under {name}.',
  'publish.phaseNone': 'No phase is set on this drawing; the version will be recorded without one.',

  'rollback.title': 'Roll back to v{counter, number, ::00}?',
  'rollback.body':
    'The newer versions stay in the history. This only changes which one is current. The change is recorded.',
  'rollback.confirm': 'Roll back',

  'remove.title': 'Remove {name}?',
  'remove.body':
    'Drawings inside it are never deleted. They move to Unfiled, where you can re-file them.',
  'remove.confirm': 'Remove',

  'error.forbidden': 'You do not have the authority for that on this project.',
  'error.gone': 'That is no longer there. The tree has been reloaded.',
  'error.conflict': 'That clashes with something already here. Reload and try again.',
  'error.rateLimited': 'Too many requests. Wait a moment and try again.',
  'error.validation': 'Something in that form was refused. Check the fields and try again.',
  'error.retryEdited': 'That looks like a retry of a different publish. Start it again.',
  'error.tooLarge': 'That file is larger than this studio allows.',
  'error.unexpected': 'Something went wrong. Nothing was published. Try again.',

  'upload.error.insecure':
    'This browser cannot hash the file over an insecure connection. Open the app over HTTPS.',
  'upload.error.unreadable': 'That file could not be read. Try choosing it again.',
  'upload.error.transfer': 'The upload was interrupted. Nothing was published. Try again.',
  'upload.error.rejected': 'That file cannot be published. Check it and upload another.',
  'upload.error.refused': 'That file was refused. Check the format and the size.',
  'upload.error.slow': 'The check is taking longer than usual. The file is stored; try again shortly.',

  // --- The conversation (`KLQ-20260820-001` phase 1) --------------------------------
  //
  // `feed.title` is "Activity", not "Comments": the stream carries published versions too,
  // and a heading that named only half of it would make the other half look like a bug.
  //
  // `feed.empty` deliberately does not offer to write the first comment — the feed is a
  // reading surface and the composer lives on a drawing. Sending somebody to a button that is
  // not on this panel is worse than saying nothing.
  //
  // `thread.emptyReadOnly` and `thread.empty` differ because the action differs: one person
  // can start the conversation and the other cannot, and one sentence for both would either
  // invite a refusal or hide an affordance.
  'feed.title': 'Activity',
  'feed.scope.project': 'Whole project',
  'feed.loading': 'Loading activity…',
  'feed.empty': 'Nothing has happened here yet.',
  'feed.noMatches': 'Nothing here matches that.',
  'feed.searchLabel': 'Search this activity',
  'feed.searchPlaceholder': 'Search comments, notes and drawing numbers…',
  'feed.more': 'Show older',
  'feed.loadingMore': 'Loading…',
  // "Published", not "r04": the row already prints the version's full server-minted name,
  // and a badge that re-rendered the counter would be this codebase's second implementation
  // of a grammar that has exactly one.
  'feed.badge.version': 'Published',
  'feed.badge.pinned': 'Pinned',
  'feed.badge.resolved': 'Resolved',
  'feed.error.cursor': 'That list moved on. Reload it to see the latest.',

  // --- The generator (`KLQ-20260820-004`) ------------------------------------------
  //
  // `generate.preview` is a separate press from `generate.create` on purpose, and the copy
  // says why: a generated code is printed on every drawing beneath it and locks on the first
  // publish. "Preview" then "Create 27 levels" is two decisions; one button would be one.
  //
  // `duplicate.intro` states both surprises before anybody presses anything — a copy takes the
  // shape and not the drawings, and it grants nobody access to anything.
  'generate.action': 'Generate levels',
  'generate.title': 'Generate levels',
  'generate.intro':
    'Say how many of each level, and the whole shape is created in one step. Everything stays editable afterwards.',
  'generate.kind': 'Level',
  'generate.count': 'How many',
  'generate.scheme': 'Numbering',
  'generate.prefix': 'Name prefix',
  'generate.prefixPlaceholder': 'Blok',
  'generate.belowGrade': 'Of those, below ground',
  'generate.addLevel': 'Add a level',
  'generate.removeLevel': 'Remove this level',
  'generate.discipline': 'Discipline',
  'generate.disciplineNone': 'Not set',
  'generate.disciplineMany': 'Each discipline gets its own set; the codes carry it (POH-ARC, POH-TZB).',
  // Above the discipline, because that is the order the filing tree goes in: an object
  // contains discipline folders, never the other way round.
  'generate.object': 'Object',
  'generate.objectNone': 'No object',

  'generate.slots': 'A drawing on each, for',
  'generate.noTypes': 'This project has no document types yet.',
  'generate.previewSlots':
    '{count, plural, one {# of them is a drawing} other {# of them are drawings}}, each empty and waiting for its first upload.',
  'generate.previewSkipped':
    '{count, plural, one {# drawing already exists and will be left alone} other {# drawings already exist and will be left alone}}.',
  'generate.preview': 'Preview',
  'generate.create': '{count, plural, one {Create # level} other {Create # levels}}',
  'generate.estimate': '{count, plural, one {# level} other {# levels}} will be created.',
  'generate.estimateSlots':
    '{count, plural, one {# empty drawing} other {# empty drawings}} on top of them. The limit counts both.',
  'generate.previewCount':
    '{count, plural, one {# level would be created} other {# levels would be created}}:',
  'generate.previewSample': 'A drawing filed at the deepest level would be called',
  'generate.tooMany':
    '{count} levels is a lot for one project. Check the counts before creating them.',

  // `group.previewIdentifiers` is a warning, not a note: a level above a drawing is part of
  // its address, so interposing one renames every drawing beneath it. That is legitimate and
  // sometimes necessary — and it is never something to discover afterwards.
  'group.action': 'Put inside a new level',
  'group.title': 'New level above',
  'group.intro': 'A new level above {names}. They keep everything they contain.',
  'group.name': 'Name of the new level',
  'group.namePlaceholder': 'Ulica, zóna, etapa…',
  'group.create': 'Create and move',
  'group.previewMoved':
    '{count, plural, one {# level moves inside the new one} other {# levels move inside the new one}}.',
  'group.previewIdentifiers':
    '{count, plural, one {# drawing changes its name} other {# drawings change their names}}, because the new level becomes part of their address.',

  'duplicate.action': 'Duplicate',
  'duplicate.title': 'Duplicate {name}',
  'duplicate.intro':
    'Copies this level and the levels beneath it. Drawings are not copied, and nobody gains access to the copy.',
  'duplicate.count': 'How many copies',

  // --- The slicer (`KLQ-20260820-005`) ---------------------------------------------
  //
  // Every string here is deliberately the language of *looking*, never of access. "Showing",
  // "hidden by this view", "show everything" — a slice is a selection anybody can clear, and
  // the People screen's zone scoping is the thing that actually decides who may see what.
  // Copy that blurred the two would teach people they had granted access by clicking a chip.
  'slice.label': 'Current selection',
  'slice.lead': 'Showing',
  'slice.remove': 'Remove from the selection',
  'slice.clear': 'Show everything',
  'slice.only': 'Show only {name}',
  'slice.showAll': 'Show the whole project',
  'slice.hidden': '{count, plural, one {# drawing hidden by this view} other {# drawings hidden by this view}}',
  'feed.excluded':
    '{count, plural, one {# drawing outside the current selection} other {# drawings outside the current selection}}',

  'thread.title': 'Comments',
  'thread.loading': 'Loading the conversation…',
  'thread.empty': 'No comments on this drawing yet. Start the conversation.',
  'thread.emptyReadOnly': 'No comments on this drawing yet.',
  // Wireframe 2's, verbatim, and shown only in Comment mode — it is the one place the
  // product ever says how a pin is made, and it is a lie anywhere a tap does nothing.
  'thread.emptySheet': 'No comments yet. Tap on the drawing to place a pin.',
  'thread.placeholder': 'Ask a question or leave a note about this drawing…',
  'thread.send': 'Comment',
  'thread.reply': 'Reply',
  'thread.resolve': 'Resolve',
  'thread.replyingTo': 'Replying to a comment.',
  'thread.cancelReply': 'Cancel',
  // Who the comment is for. Labelled by its values, never by the axis — "Audience: internal"
  // asks the reader to learn a word, "Our studio" tells them who will read it
  // (`MCK-20260814-001`).
  'thread.audience.label': 'Visible to',
  'thread.audience.internal': 'Our studio',
  'thread.audience.project': 'Whole project',
  'thread.carriedFrom': 'from v{version}',
  'thread.centrePin': 'Pin {number, number}: show it on the drawing',
  'thread.sort': 'Sort',
  'thread.sortNewest': 'Newest first',
  'thread.sortOldest': 'Oldest first',
  'thread.filter': 'Filter',
  'thread.filterAll': 'All',
  'thread.filterOpen': 'Open',
  'thread.filterResolved': 'Resolved',
  // The merged control of the 300px rail (#73): sort and filter in one box.
  'thread.merged': 'Show',
  'thread.mergedNewestAll': 'Newest · all',
  'thread.mergedNewestOpen': 'Newest · open',
  'thread.mergedNewestResolved': 'Newest · resolved',
  'thread.mergedOldestAll': 'Oldest · all',
  'thread.mergedOldestOpen': 'Oldest · open',
  'thread.mergedOldestResolved': 'Oldest · resolved',

  // --- C3, the viewer (`KLQ-20260820-001` phase 3) ---------------------------------
  //
  // The rendition states are the copy ADR-0006 asks for: the gap between "published" and
  // "viewable" must read as a stage of the work, not as a delay nobody explained. So each
  // one says what is happening, roughly how long it takes, and what can be done meanwhile —
  // and `viewer.failed.*` is careful never to call the drawing broken, because it is not: the
  // PDF still downloads and the record document is intact.
  'detail.open': 'Open drawing',
  // The way out of C3, back to the tree. §6.6 rules "Folder" out and the hierarchy is named
  // by what it is — this crumb names the tab it returns to, which is what the wireframe's
  // "‹ Back to Drawings" says and what `router.tsx` shaped the route around.
  'viewer.backToDrawings': 'Back to drawings',
  'viewer.current': 'Current',
  'viewer.optionCurrent': '{name} (current)',
  'viewer.versionPicker': 'Choose a version',
  'viewer.superseded': 'A newer version has been published: {name}.',
  'viewer.openCurrent': 'Open it',
  'viewer.truncated': 'First pages only',
  'viewer.pageOf': 'Page {page, number} / {count, number}',
  'viewer.previousPage': 'Previous page',
  'viewer.nextPage': 'Next page',
  // Two segments, not three. Review is `MCK-20260521-007` and is absent, not disabled.
  'viewer.mode.label': 'What a press on the drawing does',
  'viewer.mode.view': 'View',
  'viewer.mode.comment': 'Comment',

  // --- The pin overlay (`KLQ-20260820-001` phase 4) ---------------------------------
  //
  // A marker's only text is its number, so its accessible name has to carry the rest: a
  // screen reader landing on a bare "3" learns nothing about what it is on. The card's
  // matching control says what pressing it *does*, because from the rail the pin is not
  // visible and "Pin 3" alone would not explain why the drawing moved.
  'pin.label': 'Pin {number, number}',
  // The composer over a pending pin. `pin.send` says "Comment" and not "Place pin":
  // pressing it writes both, and a pin with nothing said about it is not what this makes.
  'pin.placeholder': 'What is the question about this point?',
  'pin.send': 'Comment',
  'pin.cancel': 'Cancel',
  // The `409` a person can actually meet: the pin is written against the slot's current
  // version, read server-side, so a publish since this tab opened refuses the placement.
  'pin.error.superseded':
    'A newer version was published while this was open, so this pin has nowhere to go. Open the current version and place it there.',

  // The measure tool's own number, drawn at the midpoint of the line it measures. A ratio and
  // not a millimetre: nothing in this product knows a drawing's scale, and the sheet's own
  // width is the one length both ends of the feature agree on (`MarkupLayer.percentOfWidth`).
  'markup.length': '{value, number} % of sheet width',

  // The tool rail (#71). Labels sit under an icon in an 84px column, so they stay one short word.
  'markup.rail.label': 'Markup tools',
  'markup.rail.show': 'Show the tools',
  'markup.rail.hide': 'Hide the tools',
  'markup.tool.pin': 'Pin',
  'markup.tool.draw': 'Draw',
  'markup.tool.line': 'Line',
  'markup.tool.box': 'Box',
  'markup.tool.text': 'Text',
  'markup.tool.measure': 'Measure',
  'markup.tool.erase': 'Erase',
  // Undo and Clear reach only what this person drew in this session (owner, 2026-09-17).
  'markup.history.label': 'Undo and clear',
  'markup.undo': 'Undo',
  'markup.clear': 'Clear',
  'markup.color.label': 'Colour',
  'markup.color.red': 'Red',
  'markup.color.blue': 'Blue',
  'markup.color.amber': 'Amber',
  'markup.color.green': 'Green',
  'markup.color.black': 'Black',
  'markup.layers.label': 'Layers',
  'viewer.phone.actions': 'Drawing actions',
  'markup.layer.pins': 'Pins',
  'markup.layer.markup': 'Markup',
  'markup.layer.tasks': 'Tasks',
  // The task pins. Named by the task rather than by a number: a task pin has none, and
  // the title is what tells two of them apart on one sheet.
  'taskPin.label': 'Task: {title}',
  'markup.text.placeholder': 'Text to place on the drawing',
  'markup.text.place': 'Place',

  'viewer.absent.title': 'Not prepared for viewing yet',
  'viewer.absent.body':
    'This version has no zoomable version. Preparing one takes under a minute for most sheets; you can download the PDF from Versions meanwhile.',
  'viewer.absent.action': 'Prepare it',
  'viewer.queued.title': 'Waiting to be prepared',
  'viewer.running.title': 'Preparing this drawing',
  'viewer.processing.body':
    'Large sheets are cut into tiles so they open instantly afterwards, however big they are. This page updates itself when it is done.',
  'viewer.failed.title': 'This drawing could not be prepared',
  'viewer.failed.body':
    'The drawing itself is fine and downloads normally. Only the zoomable version failed to build. Trying again often works; if it does not, send us the code below.',
  'viewer.failed.action': 'Try again',

  'viewer.missing.title': 'This drawing is not here',
  'viewer.missing.body':
    'It may have been removed, or it may sit outside the parts of the project shared with you.',

  'viewer.versions.title': 'Versions',
  'viewer.versions.loading': 'Loading the history…',
  'viewer.versions.empty': 'This drawing has no published versions.',
  'viewer.versions.published': 'Published {at, date, medium}',
  'viewer.publishedBy': 'Published by {who}, {when}',
  'viewer.unknownAuthor': 'Someone no longer on this project',
  'viewer.unnamedFile': 'Unnamed file',
} as const;

export type DrawingsMessageKey = keyof typeof drawingsEn;
